import {useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {useClientV2} from "../../../../utils/hooks";
import {GET_STORE_SUBSCRIPTION_SUBSCRIBERS_RQ_KEY} from "../constants";
import type {
  GetSubscriptionSubscribersApiRawResponse,
  GetSubscriptionSubscribersApiResponse,
  SubscriptionSubscriberListItem,
} from "./types";

/** Default matches customers table pagination (`SubscriptionCustomerInsights`). */
export const SUBSCRIPTION_SUBSCRIBERS_PAGE_SIZE = 10;

function parseRevenueByCurrencyRaw(raw: unknown): Record<string, number> | undefined {
  if (raw === undefined || raw === null) {
    return undefined;
  }
  if (typeof raw !== "object" || Array.isArray(raw)) {
    return undefined;
  }
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === "number" && Number.isFinite(v)) {
      out[k] = v;
      continue;
    }
    if (typeof v === "string" && v.trim() !== "") {
      const n = Number(v);
      if (Number.isFinite(n)) {
        out[k] = n;
      }
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function normalizeSubscriberRow(
  row: SubscriptionSubscriberListItem
): SubscriptionSubscriberListItem {
  const cur = typeof row.currency === "string" ? row.currency.trim() : "";

  return {
    ...row,
    customerFirstName:
      typeof row.customerFirstName === "string"
        ? row.customerFirstName.trim()
        : String(row.customerFirstName ?? "").trim(),
    customerLastName:
      typeof row.customerLastName === "string"
        ? row.customerLastName.trim()
        : String(row.customerLastName ?? "").trim(),
    customerEmail:
      typeof row.customerEmail === "string"
        ? row.customerEmail.trim()
        : String(row.customerEmail ?? "").trim(),
    tierName:
      typeof row.tierName === "string"
        ? row.tierName.trim()
        : String(row.tierName ?? "").trim(),
    currency: cur ? cur.toUpperCase() : cur,
    price: typeof row.price === "string" ? row.price.trim() : String(row.price ?? ""),
    statusName:
      typeof row.statusName === "string"
        ? row.statusName.trim()
        : String(row.statusName ?? "").trim(),
  };
}

/** `GET analytics/subscriptions/customer-purchases/:subscriptionId` */
function normalizeSubscriptionSubscribersResponse(
  response: GetSubscriptionSubscribersApiRawResponse,
  requestedPage: number,
  requestedPerPage: number
): GetSubscriptionSubscribersApiResponse {
  const d = response.data ?? ({} as GetSubscriptionSubscribersApiRawResponse["data"]);
  const rawRows = d.data ?? [];
  const subscribers = rawRows.map((row) => normalizeSubscriberRow(row));

  const perPage =
    typeof d.perPage === "number" && Number.isFinite(d.perPage)
      ? d.perPage
      : requestedPerPage;
  const page =
    typeof d.page === "number" && Number.isFinite(d.page) ? d.page : requestedPage;
  const total = typeof d.total === "number" && Number.isFinite(d.total) ? d.total : 0;
  const totalPages =
    d.totalPages ??
    (total > 0 ? Math.max(1, Math.ceil(total / Math.max(perPage, 1))) : 0);

  const hasNextPage =
    typeof d.hasNextPage === "boolean"
      ? d.hasNextPage
      : page < totalPages && totalPages > 0;
  const hasPrevPage = typeof d.hasPrevPage === "boolean" ? d.hasPrevPage : page > 1;

  const revenueByCurrency = parseRevenueByCurrencyRaw(
    d.revenueByCurrency ?? d.revenue_by_currency
  );

  const totalSalesRaw = d.totalSales ?? d.total_sales;
  const totalSales =
    typeof totalSalesRaw === "number" && Number.isFinite(totalSalesRaw)
      ? totalSalesRaw
      : undefined;

  return {
    subscribers,
    total,
    totalSales,
    revenueByCurrency,
    page,
    perPage,
    totalPages,
    hasNextPage,
    hasPrevPage,
  };
}

export const useGetSubscriptionSubscribersApi = ({
  currentPage,
  subscriptionId,
  perPage = SUBSCRIPTION_SUBSCRIBERS_PAGE_SIZE,
}: {
  currentPage: number;
  subscriptionId?: number;
  perPage?: number;
}) => {
  const client = useClientV2();

  return useQuery<
    GetSubscriptionSubscribersApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [
      GET_STORE_SUBSCRIPTION_SUBSCRIBERS_RQ_KEY,
      String(subscriptionId),
      currentPage,
      perPage,
    ],
    queryFn: async () => {
      if (!subscriptionId) {
        return {
          subscribers: [],
          total: 0,
          page: currentPage,
          perPage,
          totalPages: 0,
          hasNextPage: false,
          hasPrevPage: false,
        };
      }

      const qp = new URLSearchParams({
        page: String(currentPage),
        perPage: String(perPage),
      });
      const response = await client(
        `analytics/subscriptions/customer-purchases/${subscriptionId}?${qp}`
      );

      return normalizeSubscriptionSubscribersResponse(
        response as GetSubscriptionSubscribersApiRawResponse,
        currentPage,
        perPage
      );
    },
    enabled: !!subscriptionId,
    keepPreviousData: true,
  });
};
